import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cookieManager } from '../../utils/cookieManager';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentProps {
  onAccept?: (preferences: CookiePreferences) => void;
  onReject?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // 必要Cookie始终启用
    functional: false,
    analytics: false,
    marketing: false,
  });
  const [browserInfo, setBrowserInfo] = useState<{
    isKnownBrowser: boolean;
    fingerprint: string;
    storedCount: number;
  } | null>(null);

  useEffect(() => {
    // 检查是否已经设置过Cookie偏好
    const hasConsent = cookieManager.hasConsent();
    const browserInfo = cookieManager.getBrowserInfo();
    
    setBrowserInfo(browserInfo);
    
    if (!hasConsent) {
      setShowBanner(true);
      console.log('Cookie consent required:', {
        isKnownBrowser: browserInfo.isKnownBrowser,
        fingerprint: browserInfo.fingerprint,
        storedCount: browserInfo.storedCount,
      });
    } else {
      console.log('Cookie consent already given for this browser');
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
    setShowBanner(false);
    onAccept?.(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyNecessary);
    savePreferences(onlyNecessary);
    setShowBanner(false);
    onReject?.();
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowBanner(false);
    setShowPreferences(false);
    onAccept?.(preferences);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    // 使用cookieManager保存偏好设置，它会自动处理浏览器指纹
    cookieManager.setPreferences(prefs);
  };

  const handlePreferenceChange = (category: keyof CookiePreferences, value: boolean) => {
    if (category === 'necessary') return; // 必要Cookie不能禁用
    setPreferences(prev => ({
      ...prev,
      [category]: value,
    }));
  };

  const cookieCategories = [
    {
      key: 'necessary' as keyof CookiePreferences,
      title: '必要Cookie',
      description: '这些Cookie对于网站的基本功能是必需的，无法禁用。包括安全、身份验证和基本功能Cookie。',
      required: true,
    },
    {
      key: 'functional' as keyof CookiePreferences,
      title: '功能Cookie',
      description: '这些Cookie增强了网站的功能，如记住您的偏好设置、语言选择和个性化内容。',
      required: false,
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      title: '分析Cookie',
      description: '这些Cookie帮助我们了解网站的使用情况，改进用户体验和网站性能。',
      required: false,
    },
    {
      key: 'marketing' as keyof CookiePreferences,
      title: '营销Cookie',
      description: '这些Cookie用于提供相关广告和营销内容，跟踪广告效果和用户参与度。',
      required: false,
    },
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-cookie-bite text-teal-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Cookie设置</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                我们使用Cookie来改善您的浏览体验、提供个性化内容和分析网站流量。
                点击"接受全部"即表示您同意我们使用所有Cookie，或点击"偏好设置"来自定义您的选择。
              </p>
              {browserInfo && (
                <div className="mt-2 text-xs text-gray-500">
                  {browserInfo.isKnownBrowser ? (
                    <span className="text-green-600">
                      ✓ 检测到已知浏览器，但需要重新确认Cookie偏好
                    </span>
                  ) : (
                    <span className="text-blue-600">
                      ℹ 首次访问此浏览器，请设置Cookie偏好
                    </span>
                  )}
                </div>
              )}
              <div className="mt-3">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="text-teal-600 hover:text-teal-700 text-sm font-medium underline"
                >
                  了解更多关于我们如何使用Cookie
                </button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:ml-6">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                拒绝全部
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 border border-teal-300 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium"
              >
                偏好设置
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                接受全部
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-cookie-bite text-teal-600"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Cookie偏好设置</h2>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="fas fa-times text-gray-600"></i>
                </button>
              </div>

              {/* Description */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-800 text-sm">
                  我们尊重您的隐私选择。您可以选择接受或拒绝不同类型的Cookie。
                  请注意，禁用某些Cookie可能会影响网站的功能。
                </p>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-6">
                {cookieCategories.map((category) => (
                  <div key={category.key} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{category.title}</h3>
                        {category.required && (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            必需
                          </span>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences[category.key]}
                          onChange={(e) => handlePreferenceChange(category.key, e.target.checked)}
                          disabled={category.required}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                      </label>
                    </div>
                    <p className="text-gray-600 text-sm">{category.description}</p>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  拒绝全部
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  保存偏好设置
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
